import React, { memo, useMemo, useCallback, DragEvent } from 'react';
import { CourseBuilder } from '../types';
import { BlockContainer } from '../BlockContainer';
import './index.scss';
import classNames from 'classnames';
import { formGroup, formControl, FormDataType } from '@core/utils/form';
import { defineBlockAdvanceForm } from '../Common/AdvanceSettings';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import { useConfigureBlock } from '@modules/course/CourseBuilder/hooks';
import { DrillBlockSettings } from './DrillBlockSettings';
import { UploadAPI } from '@business/api/upload';
import { tap } from 'rxjs/operators';
import { Container } from 'reactstrap';
import { FormHTMLInline } from '@core/components/form';
import { DropableImage } from '@modules/common/DropableImage';
import { CourseBuilderService } from '@business/services';
import { R } from '@core/utils/r';
import { useCourseBuilderContext } from '../../CourseBuilderPage';
import { css } from '@emotion/react';

export const DRILL_BLOCK_AI_INSTRUCTION = `
DRILL BLOCK - AI Instruction Format for Widget Variations

The "drill" block includes interactive, test-like widgets. These are used for assessments, practice, and evaluation inside LMS steps.

Supported Types:
1. "scenarioDrill" — Multiple-choice quiz based on scenario
2. "sequenceDrill" — Sequencing items in the correct order
3. "statementDrill" — True/false or explanation statement validation
4. "flashCard" — Flashcards with optional multiple answers
5. "essayDrill" — Essay entry with optional review toggle
6. "listMatching" — Match each item to a corresponding option
7. "multipleChoiceTest" — Standard multiple-choice questions

Expected JSON Structure:
- type: "scenarioDrill" | "sequenceDrill" | "statementDrill" | "flashCard" | "essayDrill" | "listMatching" | "multipleChoiceTest"
- variation: same as type
- data: {
    (fields vary depending on the type — examples below)
  }

Examples:

✅ scenarioDrill:
{
  "type": "scenarioDrill",
  "variation": "scenarioDrill",
  "data": {
    "order": "random",
    "scenarios": [
      {
        "scenario": "What is the capital of France?",
        "answers": [
          { "option": "Berlin" },
          { "option": "Madrid" }
        ],
        "correntAnswer": "Paris",
        "correntAnswerIndex": 2,
        "answer": ""
      }
    ]
  }
}

✅ sequenceDrill:
{
  "type": "sequenceDrill",
  "variation": "sequenceDrill",
  "data": {
    "sequence": [
      {
        "description": "Step 1: Gather materials",
        "items": [
          { "item": "item 1" },
          { "item": "item 2" }
        ]
      }
    ]
  }
}

✅ statementDrill:
{
  "type": "statementDrill",
  "variation": "statementDrill",
  "data": {
    "stepTitle": "Step Title",
    "statement": "<div>Validate this statement.</div>"
  }
}

✅ flashCard:
{
  "type": "flashCard",
  "variation": "flashCard",
  "data": {
    "order": "random",
    "mainInstructions": "Match the correct term.",
    "flashCards": [
      {
        "flashCard": "What does HTML stand for?",
        "flashCardInstructions": "Select one",
        "flashCardInstructionDetails": "<div>Select the correct acronym.</div>",
        "answers": [
          { "option": "Hyper Trainer Marking Language" },
          { "option": "HyperText Markup Language" },
          { "option": "HyperText Making Links" }
        ],
        "correntAnswer": "HyperText Markup Language",
        "correntAnswerIndex": 1,
        "answer": "",
        "incorrectAnswerResponse": "<div>Try again. HTML stands for HyperText Markup Language.</div>"
      }
    ],
    "completionMessage": "<div>Well done!</div>"
  }
}

✅ essayDrill:
{
  "type": "essayDrill",
  "variation": "essayDrill",
  "data": {
    "stepTitle": "Reflect on Today's Lesson",
    "instructions": "<div>Write a short essay explaining what you learned today.</div>",
    "requireReview": true,
    "isSubmitted": false,
    "essay": null,
    "files": null
  }
}

✅ listMatching:
{
  "type": "listMatching",
  "variation": "listMatching",
  "data": {
    "instructions": "<div>Instructions</div>",
    "items": [
      {
        "item": "Step 1: Gather materials",
        "matchingItem": "Step 1: Gather materials"
      }
    ]
  }
}

✅ multipleChoiceTest:
{
  "type": "multipleChoiceTest",
  "variation": "multipleChoiceTest",
  "data": {
    "order": "random",
    "scenarios": [
      {
        "scenario": "What is the capital of France?",
        "answers": [
          { "option": "Berlin" },
          { "option": "Madrid" }
        ],
        "correntAnswer": "Paris",
        "correntAnswerIndex": 2,
        "answer": ""
      }
    ]
  }
}
`;

function defineDrillBlockForm() {
  return formGroup({
    text: formControl<string>({
      initialValue: `<ol><li>Vel magna iaculis arcu cursus parturient blandit suscipit taciti auctor maecenas.</li><li>Penatibus libero risus montes conubia pretium quisque ut fermentum, vulputate augue litora ornare fames turpis convallis imperdiet, curae etiam sed enim dictumst purus sit.</li><li>Tortor parturient gravida habitant dapibus et fusce felis nam nibh litora ridiculus aenean porttitor turpis pharetra.</li><li>Suscipit montes natoque aptent ex gravida orci ultrices volutpat nunc.</li></ol>`,
    }),
    advance: defineBlockAdvanceForm(),
  });
}

export type DrillWidgetFormData = FormDataType<typeof defineDrillBlockForm>;
export type DrillWidgetForm = ReturnType<typeof defineDrillBlockForm>;

export enum DrillBlockVariation {
  scenarioDrill = 'scenarioDrill',
  sequenceDrill = 'sequenceDrill',
  statementDrill = 'statementDrill',
  flashCard = 'flashCard',
  essayDrill = 'essayDrill',
}

export const DrillBlock = memo((props: CourseBuilder.BlockProps<DrillBlockVariation>) => {
  const { variation, preview, editable } = props;
  const form = useMemo(defineDrillBlockForm, []);
  const formValue = useNonNilObservable(form.value$);
  const proposal = useCourseBuilderContext();
  const primaryColor = useMemo(() => proposal?.form.value.primaryColor, [proposal]);

  const listCss = useMemo(() => {
    if (primaryColor) {
      return css`
        li:before {
          background-color: ${primaryColor};
        }
      `;
    } else {
      return css`
        li:before {
          background-color: #45a8f7;
        }
      `;
    }
  }, [primaryColor]);

  useConfigureBlock({
    props,
    form,
    contentComponent: DrillBlockSettings,
    contentProps: { form, variation },
  });

  return (
    <Container>
      <BlockContainer className={classNames('d-flex flex-fill', 'block-list', variation)} advanceForm={formValue.advance}>
        <div css={listCss}>
          <FormHTMLInline control={form.controls.text} readonly={true} glossarify />
        </div>
      </BlockContainer>
    </Container>
  );
});
